import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import useDate from "~/ui-rtk/hooks/date"
import "./style.scss"
import { Button } from "~/ui-rtk/components/ui/controls"
import { motion, AnimatePresence } from "framer-motion"
import { Logo } from "~/ui-rtk/components/ui/common"

type HighlightType = "company" | "category" | "product"

interface HighlightItem {
  text: string
  type: HighlightType
}

interface HighlightMatch {
  start: number
  end: number
  item: HighlightItem
}

interface TextSegment {
  text: string
  isHighlighted: boolean
  type?: HighlightType
}

const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const findMatches = (
  text: string,
  items: HighlightItem[],
): HighlightMatch[] => {
  const matches: HighlightMatch[] = []

  items.forEach(item => {
    const pattern = new RegExp(`\\b(${escapeRegExp(item.text)})\\b`, "gi")
    let match
    while ((match = pattern.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        item,
      })
    }
  })

  return matches.sort((a, b) => a.start - b.start)
}

// Typing indicator component (bouncing dots)
const TypingIndicator: React.FC = () => (
  <div className="flex items-center space-x-1 py-2">
    <div className="flex space-x-1">
      <motion.div
        className="w-2 h-2 bg-white rounded-full"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        className="w-2 h-2 bg-white rounded-full"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        className="w-2 h-2 bg-white rounded-full"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  </div>
)

const TypingText: React.FC<{
  segments: TextSegment[]
  onComplete?: () => void
}> = ({ segments, onComplete }) => {
  const [showTypingIndicator, setShowTypingIndicator] = useState(true)
  const [displayedText, setDisplayedText] = useState("")
  const [currentIndex, setCurrentIndex] = useState(0)
  const fullText = segments.map(s => s.text).join("")

  // Reset when segments change
  useEffect(() => {
    setShowTypingIndicator(true)
    setDisplayedText("")
    setCurrentIndex(0)

    // Show typing indicator for 1.5 seconds before starting to type
    const timer = setTimeout(() => {
      setShowTypingIndicator(false)
    }, 1500)

    return () => clearTimeout(timer)
  }, [segments])

  // Character-by-character typing effect
  useEffect(() => {
    if (!showTypingIndicator && currentIndex < fullText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(fullText.slice(0, currentIndex + 1))
        setCurrentIndex(currentIndex + 1)
      }, 10) // Adjust speed here (lower = faster)

      return () => clearTimeout(timer)
    } else if (
      !showTypingIndicator &&
      currentIndex === fullText.length &&
      onComplete
    ) {
      // Typing is complete, call the callback
      const timer = setTimeout(() => {
        onComplete()
      }, 800) // Delay before showing the grid
      return () => clearTimeout(timer)
    }
  }, [showTypingIndicator, currentIndex, fullText, onComplete])

  const renderTypedText = useCallback(() => {
    if (!displayedText) return null

    const result: React.ReactNode[] = []
    let textIndex = 0
    let segmentIndex = 0
    let segmentTextIndex = 0

    while (textIndex < displayedText.length && segmentIndex < segments.length) {
      const segment = segments[segmentIndex]
      const remainingInSegment = segment.text.length - segmentTextIndex
      const remainingToDisplay = displayedText.length - textIndex

      const charactersToTake = Math.min(remainingInSegment, remainingToDisplay)
      const segmentText = segment.text.slice(
        segmentTextIndex,
        segmentTextIndex + charactersToTake,
      )

      if (segment.isHighlighted) {
        result.push(
          <motion.span
            key={`segment-${segmentIndex}-${segmentTextIndex}`}
            className="italic decoration-2 animate-highlight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
          >
            {segmentText}
          </motion.span>,
        )
      } else {
        result.push(segmentText)
      }

      textIndex += charactersToTake
      segmentTextIndex += charactersToTake

      if (segmentTextIndex >= segment.text.length) {
        segmentIndex += 1
        segmentTextIndex = 0
      }
    }

    return result
  }, [displayedText, segments])

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative"
    >
      {showTypingIndicator ? (
        <TypingIndicator />
      ) : (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {renderTypedText()}
          {currentIndex < fullText.length && (
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="inline-block w-0.5 h-6 bg-white ml-1"
            />
          )}
        </motion.span>
      )}
    </motion.div>
  )
}

const highlightTextToSegments = (
  text: string,
  matches: HighlightMatch[],
): TextSegment[] => {
  if (matches.length === 0) return [{ text, isHighlighted: false }]

  const segments: TextSegment[] = []
  let lastIndex = 0

  matches.forEach(match => {
    if (match.start > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.start),
        isHighlighted: false,
      })
    }

    segments.push({
      text: text.slice(match.start, match.end),
      isHighlighted: true,
      type: match.item.type,
    })

    lastIndex = match.end
  })

  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      isHighlighted: false,
    })
  }

  return segments
}

const AISummary: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true)
  const [isTypingComplete, setIsTypingComplete] = useState(false)
  const dayjs = useDate()

  const baseQuery = useMemo<TCubeQuery>(
    () => ({
      dimensions: [
        "company_ai_brand_summary.company_name",
        "company_ai_brand_summary.business_category",
        "company_ai_brand_summary.products_categories",
        "company_ai_brand_summary.main_products",
        "company_ai_brand_summary.text_style",
        "company_ai_brand_summary.summary",
      ],
    }),
    [],
  )

  const brandSummaryQuery = useMemo(
    () =>
      polyfillCubeQueryWithTimeDimension(baseQuery, {
        dateRange: [
          dayjs().subtract(1, "year").startOf("day").toDate(),
          dayjs().endOf("day").toDate(),
        ],
      }),
    [baseQuery],
  )

  const { resultSet } = useCubeQuery(brandSummaryQuery)

  const brandData = useMemo(() => {
    if (!resultSet) return null
    const data = resultSet.tablePivot()
    return data[0]
  }, [resultSet])

  const highlightedSummary = useMemo(() => {
    if (!brandData) return []

    const summary = brandData?.["company_ai_brand_summary.summary"] as string
    const companyName = brandData?.[
      "company_ai_brand_summary.company_name"
    ] as string
    const businessCategory = brandData?.[
      "company_ai_brand_summary.business_category"
    ] as string
    const products = (
      brandData?.["company_ai_brand_summary.products_categories"] as string
    )
      .split(",")
      .map(product => product.trim())
      .filter(product => product.length > 0)

    // Use Set for unique keywords
    const uniqueKeywords = new Set([companyName, businessCategory, ...products])

    // Convert to highlight items
    const highlightItems: HighlightItem[] = Array.from(uniqueKeywords).map(
      keyword => {
        const nonCompanyType: HighlightType =
          keyword === businessCategory ? "category" : "product"
        return {
          text: keyword,
          type: keyword === companyName ? "company" : nonCompanyType,
        }
      },
    )

    // Find all matches in the text
    const matches = findMatches(summary, highlightItems)

    // Apply highlights
    return highlightTextToSegments(summary, matches)
  }, [brandData])

  // Reset typing state when data changes or component becomes visible
  useEffect(() => {
    setIsTypingComplete(false)
  }, [brandData, isVisible])

  return (
    <div className="flex flex-col rounded-system-xl bg-system-blue-800">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
              transition: {
                height: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.15 },
              },
            }}
            className="overflow-hidden"
          >
            <>
              <div className="flex flex-col bg-system-salad-800 p-system-lg rounded-system-xl">
                <div>
                  <div className="flex items-center space-x-system-xs mb-system-md">
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <Logo width={20} height={20} />
                    </div>
                    <h3 className="leading-none font-semibold text-system-gray-400 uppercase font-lato text-sm">
                      ai Summary
                    </h3>
                  </div>
                  <div className="text-white whitespace-pre-line font-lato text-3xl my-system-lg">
                    <TypingText
                      segments={highlightedSummary}
                      onComplete={() => setIsTypingComplete(true)}
                    />
                  </div>
                </div>
                <AnimatePresence>
                  {isTypingComplete && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{
                        duration: 0.6,
                        ease: "easeOut",
                        delay: 0.3,
                      }}
                      className="grid grid-cols-4 gap-4 mt-system-lg"
                    >
                      <motion.div
                        className="border-r border-system-gray-800 pr-system-lg"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                      >
                        <h3 className="leading-none font-semibold text-system-gray-400 uppercase font-lato text-sm">
                          Business Category
                        </h3>
                        <p className="text-white font-lato mt-system-md">
                          {
                            brandData?.[
                              "company_ai_brand_summary.business_category"
                            ]
                          }
                        </p>
                      </motion.div>
                      <motion.div
                        className="border-r border-system-gray-800 pr-system-lg"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.7 }}
                      >
                        <h3 className="leading-none font-semibold text-system-gray-400 uppercase font-lato text-sm">
                          Product Categories
                        </h3>
                        <p className="text-white font-lato mt-system-md">
                          {
                            brandData?.[
                              "company_ai_brand_summary.products_categories"
                            ]
                          }
                        </p>
                      </motion.div>
                      <motion.div
                        className="border-r border-system-gray-800 pr-system-lg"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.9 }}
                      >
                        <h3 className="leading-none font-semibold text-system-gray-400 uppercase font-lato text-sm">
                          Main Products
                        </h3>
                        <p className="text-white font-lato mt-system-md">
                          {
                            brandData?.[
                              "company_ai_brand_summary.main_products"
                            ]
                          }
                        </p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 1.1 }}
                      >
                        <h3 className="leading-none font-semibold text-system-gray-400 uppercase font-lato text-sm">
                          Text Style
                        </h3>
                        <p className="text-white font-lato mt-system-md">
                          {brandData?.["company_ai_brand_summary.text_style"]}
                        </p>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex p-system-lg justify-center">
        <Button
          onClick={() => setIsVisible(!isVisible)}
          className="px-system-xl"
        >
          {isVisible ? "Hide" : "Show"} Summary
        </Button>
      </div>
    </div>
  )
}
export default AISummary
